import {isFeatureEnabled} from 'feature-flags';
import {openDialog} from 'platform/components';

import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {useGetBranchV2Query} from '@dms/api/base';
import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {useIsLicenceEnabled, usePermissions} from '@dms/shared';

import {useNavigate} from 'shared/web';

import {DeactivateBranchDialogContent} from '../../components/DeactivateBranchDialogContent/DeactivateBranchDialogContent';
import {MANAGER_ROLE_NAME} from '../UserDetail/constants/MANAGER_ROLE_NAME';
import {BranchForm} from './components/BranchForm';
import {OpeningHours} from './components/OpeningHours';

const breadcrumbs = [
  {label: i18n.t('entity.branch.labels.branches'), href: settingsRoutes.branches},
];

const DELETE_ACTION = {
  type: 'button',
  variant: 'danger',
  leftIcon: 'action/delete',
  title: i18n.t('general.actions.delete'),
} as const;

export function BranchDetail() {
  const {id: branchId} = useParams();
  const navigate = useNavigate();
  const isCreating = branchId === undefined;

  const {data: currentUser} = useGetCurrentUserInfoQuery();
  const {
    data: branch,
    isLoading,
    isError,
  } = useGetBranchV2Query({id: branchId ?? ''}, {skip: isCreating});

  const redirectToOverview = () => navigate(settingsRoutes.branches);

  const handleDeleteBranch = () => {
    if (isNil(branch?.id)) {
      return;
    }
    openDialog(
      <DeactivateBranchDialogContent branchId={branch.id} onSuccess={redirectToOverview} />,
      {
        id: 'deleteBranchDialog',
        'data-testid': 'deleteBranchDialog',
        title: i18n.t('page.settings.labels.deactivateBranchTitle', {name: branch.name}),
      }
    );
  };

  const isUserAdmin = currentUser?.roles
    ? currentUser.roles.some((role) => role.title === MANAGER_ROLE_NAME)
    : false;

  const [canReadBusinessCase, canReadRentalVehicle] = usePermissions({
    permissionKeys: ['businessCaseRead', 'readRentalVehicle'],
  });

  const {isWorkshopModuleEnabled} = useIsLicenceEnabled();

  const hasAnyOpeningHoursLicence =
    canReadBusinessCase || isWorkshopModuleEnabled || canReadRentalVehicle;

  const layoutConfig = isFeatureEnabled(featureFlags.CORE_BRANCH_OPENING_TIMES)
    ? {
        headerVariant: 'v2' as const,
        header: {
          title: branch?.name ?? i18n.t('entity.branch.labels.createNewBranch'),
          breadcrumbs,
        },
        tabs: [
          {
            queryId: 'general',
            title: i18n.t('general.labels.general'),
            content: <BranchForm branchId={branchId} onDeleteBranch={handleDeleteBranch} />,
          },
          {
            queryId: 'opening-hours',
            title: i18n.t('entity.branch.labels.openingHours'),
            content: (
              <OpeningHours
                canReadBusinessCase={canReadBusinessCase}
                isWorkshopModuleEnabled={isWorkshopModuleEnabled}
                canReadRentalVehicle={canReadRentalVehicle}
              />
            ),
            isDisabled: !branchId || !hasAnyOpeningHoursLicence,
          },
        ],
        children: null,
      }
    : {
        headerVariant: undefined,
        header: {
          title: branch?.name ?? i18n.t('entity.branch.labels.createNewBranch'),
          subtitle: isNotNil(branch)
            ? `${i18n.t('entity.branch.labels.branchId')}: ${branch.id}`
            : undefined,
          breadcrumbs,
          actions: isUserAdmin ? [{...DELETE_ACTION, onClick: handleDeleteBranch}] : undefined,
        },
        tabs: undefined,
        children: <BranchForm branchId={branchId} />,
      };

  return (
    <SettingsTemplate
      isLoading={isLoading}
      isError={isError}
      isCreating={isCreating}
      header={layoutConfig.header}
      headerVariant={layoutConfig.headerVariant}
      data-testid={testIds.settings.branches('detail')}
      tabs={layoutConfig.tabs}
    >
      {layoutConfig.children}
    </SettingsTemplate>
  );
}
